import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
	type CallDirection,
	type CallOutcome,
	createPersonActivity,
	type MeetingType,
} from "../../../api/person/activity";
import type { ActivityPanelHandle, ActivityPanelProps } from "./panel-types";

interface ActivityPanelSpecificProps extends ActivityPanelProps {
	personId: number;
	mode: "call" | "meeting";
}

export const ActivityPanel = forwardRef<
	ActivityPanelHandle,
	ActivityPanelSpecificProps
>(({ personId, mode, onCanSaveChange }, ref) => {
	const [content, setContent] = useState("");
	const [happenedAt, setHappenedAt] = useState<Dayjs | null>(dayjs());
	const [callDirection, setCallDirection] = useState<CallDirection>("outbound");
	const [callOutcome, setCallOutcome] = useState<CallOutcome>("connected");
	const [meetingType, setMeetingType] = useState<MeetingType>("video");
	const [durationMinutes, setDurationMinutes] = useState<number | "">("");

	// Call/meeting is always saveable
	useEffect(() => {
		onCanSaveChange(true);
	}, [onCanSaveChange]);

	useImperativeHandle(ref, () => ({
		save: async () => {
			await createPersonActivity(personId, {
				activity_type: mode,
				content,
				happened_at: (happenedAt || dayjs()).toISOString(),
				...(mode === "call" && {
					call_direction: callDirection,
					call_outcome: callOutcome,
				}),
				...(mode === "meeting" && {
					meeting_type: meetingType,
				}),
				...(durationMinutes !== "" && {
					duration_minutes: durationMinutes,
				}),
			});
		},
	}));

	return (
		<Stack spacing={2}>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DateTimePicker
					label="When"
					value={happenedAt}
					ampm={false}
					onChange={(value) => {
						if (value) {
							setHappenedAt(dayjs.isDayjs(value) ? value : dayjs(value));
						}
					}}
					slotProps={{
						textField: { fullWidth: true },
					}}
				/>
			</LocalizationProvider>

			{mode === "call" && (
				<>
					<FormControl fullWidth data-component="call-direction-control">
						<InputLabel>Direction</InputLabel>
						<Select
							value={callDirection}
							label="Direction"
							onChange={(e) =>
								setCallDirection(e.target.value as CallDirection)
							}
							data-component="call-direction-select"
						>
							<MenuItem value="outbound">Outbound</MenuItem>
							<MenuItem value="inbound">Inbound</MenuItem>
						</Select>
					</FormControl>
					<Stack direction="row" spacing={2}>
						<FormControl fullWidth data-component="call-outcome-control">
							<InputLabel>Outcome</InputLabel>
							<Select
								value={callOutcome}
								label="Outcome"
								onChange={(e) =>
									setCallOutcome(e.target.value as CallOutcome)
								}
								data-component="call-outcome-select"
							>
								<MenuItem value="connected">Connected</MenuItem>
								<MenuItem value="voicemail">Left Voicemail</MenuItem>
								<MenuItem value="no_answer">No Answer</MenuItem>
								<MenuItem value="busy">Busy</MenuItem>
							</Select>
						</FormControl>
						<TextField
							label="Duration (min)"
							type="number"
							value={durationMinutes}
							onChange={(e) =>
								setDurationMinutes(
									e.target.value === "" ? "" : Number(e.target.value)
								)
							}
							fullWidth
							data-component="duration-input"
						/>
					</Stack>
				</>
			)}

			{mode === "meeting" && (
				<Stack direction="row" spacing={2}>
					<FormControl fullWidth data-component="meeting-type-control">
						<InputLabel>Type</InputLabel>
						<Select
							value={meetingType}
							label="Type"
							onChange={(e) =>
								setMeetingType(e.target.value as MeetingType)
							}
							data-component="meeting-type-select"
						>
							<MenuItem value="video">Video Call</MenuItem>
							<MenuItem value="in_person">In Person</MenuItem>
						</Select>
					</FormControl>
					<TextField
						label="Duration (min)"
						type="number"
						value={durationMinutes}
						onChange={(e) =>
							setDurationMinutes(
								e.target.value === "" ? "" : Number(e.target.value)
							)
						}
						fullWidth
						data-component="duration-input"
					/>
				</Stack>
			)}

			<TextField
				label="Notes"
				value={content}
				onChange={(e) => setContent(e.target.value)}
				multiline
				rows={2}
				fullWidth
				placeholder="Any additional notes about this activity..."
				data-component="content-input"
			/>
		</Stack>
	);
});

ActivityPanel.displayName = "ActivityPanel";
